import { useQueryXeroGetIntegrationStatus } from "@gymflow/api";
import { useClubSettings } from "apps/portal/src/providers";
import { ToastContext } from "apps/portal/src/providers/ToastProvider/context";
import useGymflowModels from "apps/portal/src/store";
import { pick } from "lodash";
import qs from "qs";
import { useContext, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router";

import { usePortalRoutes } from "../../../hooks";
import { RouteFeature, RouteLayout } from "../../../routes";
import { CloseIcon } from "../../atoms";
import { SettingsRoute } from "../../Settings/SettingsRoute";
import { XeroIntegrationForm } from "./components/XeroIntegrationForm/XeroIntegrationForm";

export const XeroIntegration = () => {
  const { t } = useTranslation();
  const { push } = useHistory();
  const { createClubLink } = usePortalRoutes();
  const { api } = useGymflowModels();
  const { clubId } = useClubSettings();
  const { toast } = useContext(ToastContext);

  const { data: dataXero, isLoading: isLoadingDataXero } =
    useQueryXeroGetIntegrationStatus({
      api,
      clubId,
    });

  const { authenticatedToast } = pick(
    qs.parse(window.location.search, {
      ignoreQueryPrefix: true,
    }) as { authenticatedToast?: "show" },
    ["authenticatedToast"],
  );

  const isShowAuthenticatedToast = authenticatedToast === "show";
  const isOnboarding = dataXero?.status === "ONBOARDING";

  useEffect(() => {
    if (isShowAuthenticatedToast && isOnboarding) {
      toast({
        type: "normal",
        intent: "success",
        message: t(
          "page.settings.tab.integrations.xeroIntegration.toast.xeroAuthenticated.title",
        ),
        description: t(
          "page.settings.tab.integrations.xeroIntegration.toast.xeroAuthenticated.description",
        ),
      });
    }
  }, [isOnboarding, isShowAuthenticatedToast, t, toast]);

  return (
    <div className="bg-gray-0 flex h-full max-h-full flex-col gap-8 p-8">
      <div className="flex max-h-full flex-col">
        <div className="flex justify-between">
          <div className="flex flex-row items-center justify-center gap-3">
            <div className="flex">
              <div className="bg-xero-logo size-12 rounded-lg bg-contain bg-center bg-no-repeat" />
            </div>
            <div className="flex flex-col justify-center">
              <div className="text-xl font-semibold text-gray-950">
                {t("page.settings.tab.integrations.xeroIntegration.title")}
              </div>
              <div className="text-sm font-medium text-gray-500">
                {t(
                  "page.settings.tab.integrations.xeroIntegration.description",
                )}
              </div>
            </div>
          </div>
          <div className="mr-4 flex">
            <div
              className="cursor-pointer"
              onClick={() => {
                push(
                  createClubLink(
                    RouteLayout.Staff,
                    RouteFeature.Settings,
                    SettingsRoute.Integrations,
                  ),
                );
              }}
            >
              <CloseIcon className="size-6" pathClassName="stroke-gray-500" />
            </div>
          </div>
        </div>
      </div>
      <div className="flex h-full max-h-full flex-col gap-4 overflow-y-auto">
        {!isLoadingDataXero && (
          <div className="flex flex-col gap-0.5">
            <div className="text-lg font-semibold text-gray-950">
              {isOnboarding
                ? t(
                    "page.settings.tab.integrations.xeroIntegration.setupIntegration",
                  )
                : t(
                    "page.settings.tab.integrations.xeroIntegration.editIntegration",
                  )}
            </div>
            <div className="text-sm font-medium text-gray-500">
              {t(
                "page.settings.tab.integrations.xeroIntegration.pleaseSelectAccounts",
              )}
            </div>
          </div>
        )}
        <XeroIntegrationForm isOnboarding={isOnboarding} />
      </div>
    </div>
  );
};
